'use client'

import { nanoid } from 'nanoid'
import { findIndex } from 'lodash'
import { useTranslations } from 'next-intl'
import { useSearchParams } from 'next/navigation'

import type { ArtifactAction, Asset, ProjectMessage } from '@/lib/types'
import { parseAIStream } from '@/lib/stream'
import { usePreviewStore } from '@/hooks/use-preview-store'
import { useProjectStore } from '@/hooks/use-project-store'
import { usePlaySound } from '@/hooks/use-play-sound'
import { pushModal } from '@/components/modals'
import { useAiCodingMonitor } from '@/hooks/use-ai-coding-monitor'
import { mutate } from 'swr/_internal'
import { sleep } from '@/lib/sleep'
import { alertError } from '@/lib/alert-error'
import { usePlayConfetti } from '@/contexts/confetti'
import { env } from 'next-runtime-env'
import { useEffect, useRef } from 'react'
import sendToBothAnalytics from '@/lib/mixpanel/sendToBothAnalytics'
import { MIXPANEL_CLIENT_EVENT_NAMES } from '@/lib/mixpanel/constant'
import { useCurrentUser } from '@/hooks/use-current-user'

import { toast } from 'sonner'
import { useSuperPromptsStore } from './use-super-prompts-store'
import { useStreamDebugStore } from '@/hooks/use-stream-debug-store'

const COMPLETED_FLAG = '<System>Completed</System>'
const INITIALIZATION_FLAG = '<System>Initialization</System>'
const CODING_SUCCESS_FLAG = '<System>Coding successful</System>'

export const useAICoding = () => {
  const {
    addMessage,
    removeTemporaryMessages,
    updateMessage,
    deleteMessage,
    deleteRetryMessage,
    deleteRecentCodingMessages,
    getMessagesToSentAI,
    saveMessagesSnapshot,
    restoreMessagesSnapshot,
    checkoutVersion,
    setChatExpanded,
    setAiStage,
    setAiCodingType,
    getInitialCodingStage,
    setIsFixingIssue,
    getKnowledgeIds,
    getProject,
    project,
  } = useProjectStore()
  const { updatePreview, setProcessingText, setStage } = usePreviewStore()
  const { monitorResponseFailure } = useAiCodingMonitor()
  const playSound = usePlaySound()
  const codingTaskApiUrl = env('NEXT_PUBLIC_CODING_TASK_API_URL')

  const isCanceled = useRef(false)

  const { show: playConfetti } = usePlayConfetti()

  const searchParams = useSearchParams()
  const projectId = searchParams.get('id')
  const abortController = useRef(new AbortController())

  const t = useTranslations('ai-chat.ai-coding')

  const { setCodingSuperPromptIds, clearCodingSuperPromptIds } =
    useSuperPromptsStore()
  const { data: user } = useCurrentUser()
  const didTrackProjectGenerationRef = useRef(false)
  const lastTrackedProjectIdRef = useRef<string | null>(null)
  const currentProjectId = project?._id?.toString()

  useEffect(() => {
    if (!currentProjectId) {
      didTrackProjectGenerationRef.current = false
      lastTrackedProjectIdRef.current = null
      return
    }

    if (lastTrackedProjectIdRef.current !== currentProjectId) {
      didTrackProjectGenerationRef.current = false
      lastTrackedProjectIdRef.current = currentProjectId
    }
  }, [currentProjectId])

  const aiCoding = async (options?: {
    regenerate?: boolean
    retry?: boolean
    addedUserMessage?: ProjectMessage
    codingType?: string
    onFirstResponse?: () => void
    assets?: Asset[]
  }) => {
    const {
      regenerate = false,
      retry = false,
      addedUserMessage,
      codingType = '',
      onFirstResponse,
      assets,
    } = options || {}

    if (!projectId) {
      throw new Error('projectId is required')
    }
    const heyBossMessage: ProjectMessage = {
      id: nanoid(),
      role: 'system',
      type: 'loading',
      timestamp: Date.now(),
      ...(addedUserMessage?.cardType && {
        cardType: addedUserMessage.cardType,
        cardData: addedUserMessage.cardData,
      }),
    }
    // Always show loading message immediately to provide instant feedback
    addMessage(heyBossMessage)
    setAiStage('coding')
    setAiCodingType(codingType)
    const userMessage = getMessagesToSentAI()
    const messageToSend = userMessage
      ? {
          ...userMessage,
          ...(assets ? { assets } : {}),
        }
      : userMessage

    const response = await fetch(`/api/coding/${projectId}`, {
      method: 'POST',
      // Taking the 2nd message and avoiding the file system tree message(using file system tree from backend instead)
      body: JSON.stringify({
        message: messageToSend,
        regenerate,
        knowledge_ids: getKnowledgeIds(),
      }),
      headers: {
        'x-request-id': messageToSend?.id || nanoid(12),
      },
    })
    if (response.status !== 200) {
      let error, message, key
      try {
        const errorData = await response.json()
        error = errorData.error
        message = errorData.message
        key = errorData.key
      } catch (jsonError) {
        // Handle cases where response body is not valid JSON
        console.error('Failed to parse error response:', jsonError)
        error = `HTTP ${response.status}: ${response.statusText || 'Request failed'}`
        message = 'The server returned an invalid response. Please try again.'
      }

      if (key === 'INSUFFICIENT_COINS') {
        pushModal('PricingModal', {
          type: 'out_of_credit',
        })
        setAiStage('idle')
        return
      }
      if (
        key === 'WORKSPACE_INSUFFICIENT_COINS_OWNER' ||
        key === 'WORKSPACE_INSUFFICIENT_COINS'
      ) {
        // Workspace owner/admin: show pricing modal
        pushModal('PricingModal', {
          type: 'out_of_credit',
          workspaceId: project?.workspace_id?.toString(),
        })
        setAiStage('idle')
        return
      }
      if (key === 'WORKSPACE_INSUFFICIENT_COINS_MEMBER') {
        // Non-admin/owner member: show contact admin modal
        pushModal('WorkspaceInsufficientCoinsModal', {
          workspaceId: project?.workspace_id?.toString(),
        })
        setAiStage('idle')
        return
      }
      if (key === 'PROJECT_SIZE_EXCEEDED') {
        pushModal('AlertModal', {
          type: 'project_size_exceeded',
        })
        setAiStage('idle')
        return
      }
      alertError('createAiCodingTask error', { error, message })
      addMessage({
        id: nanoid(),
        role: 'system',
        type: 'retry',
      })
      setAiStage('idle')
      toast.error('Operation failed', {
        description: error || message,
        duration: 3000,
      })
      return
    }
    const { taskId } = await response.json()
    if (taskId) {
      isCanceled.current = false
      await sleep(1e3)
      retrieveCodingTask(projectId, {
        regenerate,
        retry,
        addedUserMessage,
        messageToUpdate: heyBossMessage,
        onFirstResponse,
      })
    }
  }

  const retrieveCodingTask = async (
    projectId: string,
    options?: {
      regenerate?: boolean
      addedUserMessage?: ProjectMessage
      messageToUpdate?: ProjectMessage
      retry?: boolean
      onSuccess?: () => void
      onFirstResponse?: () => void
    },
  ) => {
    if (!projectId) {
      return
    }

    const retrieveCodingTaskRes = await fetch(`/api/coding/${projectId}`, {
      method: 'GET',
    })
    const responseData = await retrieveCodingTaskRes.json()

    const { taskId, token } = responseData
    if (!taskId) {
      return
    }

    const { retry = false, regenerate = false } = options || {}
    const { addedUserMessage, messageToUpdate, onFirstResponse } = options || {}
    const isRegenerate = !!regenerate

    // Use messageToUpdate's ID if available, otherwise generate new one
    // This allows us to update the loading message in place
    const artifactMessageId = messageToUpdate?.id || nanoid()

    const improvingAction: ArtifactAction = {
      key: nanoid(),
      content: 'Improving implementation...',
      status: 'in_progress',
      type: 'improving',
    }

    const requestId = nanoid(8)
    let isCompleted = false
    let isSuccess = false
    let isInitialization = false
    let aiStream: ReadableStream | null = null
    let streamContent = ''
    let hasFirstResponse = false
    let previewMessageId: string | null = null
    let lastPreviewContent = ''

    // Clear debug stream content at start of new coding task
    const debugStore = useStreamDebugStore.getState()
    if (debugStore.isEnabled) {
      debugStore.clearContent()
    }

    /** To make sure we don't duplicate preview message in one run */
    const resolveExistingPreviewId = () => {
      const project = getProject()
      const previews =
        project?.messages?.filter(
          (m) => m.type === 'preview_complete' && !m.hidden,
        ) || []
      return previews.length ? previews[previews.length - 1].id : null
    }

    /** This is a frontend fix for display, backend (coding-task) should be responsible for removing these tags when writing to DB */
    const cleanAssistantText = (text: string) =>
      (text || '')
        .replace(/<System>[\s\S]*?<\/System>/g, '')
        .replace(/<UserMessage>[\s\S]*?<\/UserMessage>/g, '')
        .replace(/<\/?assistant>/g, '')
        .trim()

    const upsertPreviewMessage = (
      content: string,
      options?: { isFinal?: boolean },
    ) => {
      const cleanContent = cleanAssistantText(content)

      if (!cleanContent || cleanContent === lastPreviewContent) {
        return
      }

      lastPreviewContent = cleanContent
      const timestamp = Date.now()

      // Only reuse existing preview_complete message on RECONNECT (ing state)
      // For new coding tasks, always create a fresh message
      if (!previewMessageId) {
        const isReconnect = getInitialCodingStage() === 'ing'
        if (isReconnect) {
          previewMessageId = resolveExistingPreviewId() || nanoid()
        } else {
          // For new tasks, hide any existing preview_complete messages first
          // to avoid duplicates when checkoutVersion loads from backend
          const existingPreviewId = resolveExistingPreviewId()
          if (existingPreviewId) {
            updateMessage(existingPreviewId, (msg) => {
              msg.hidden = true
            })
          }
          previewMessageId = nanoid()
        }
      }

      const project = getProject()
      const targetExists = project?.messages?.some(
        (m) => m.id === previewMessageId,
      )

      if (!targetExists) {
        addMessage({
          id: previewMessageId,
          role: 'assistant',
          type: 'preview_complete',
          content: cleanContent,
          timestamp,
        })
        return
      }

      updateMessage(previewMessageId, (message) => {
        message.role = 'assistant'
        message.type = 'preview_complete'
        message.content = cleanContent
        if (options?.isFinal) {
          message.timestamp = timestamp
        }
      })
    }

    const extractAssistantContent = (text: string) => {
      const assistantStart = text.indexOf('<assistant>')
      if (assistantStart === -1) {
        return ''
      }
      const afterAssistant = text.slice(assistantStart + '<assistant>'.length)
      const assistantEnd = afterAssistant.indexOf('</assistant>')
      const assistantContent =
        assistantEnd !== -1
          ? afterAssistant.slice(0, assistantEnd)
          : afterAssistant
      return cleanAssistantText(assistantContent)
    }

    try {
      abortController.current = new AbortController()
      // retrieve coding streaming
      const response = await fetch(
        `${codingTaskApiUrl}/api/coding/${projectId}?token=${token}`,
        {
          method: 'GET',
          signal: abortController.current.signal,
        },
      )

      if (response.status !== 200) {
        let error: any
        try {
          error = await response.json()
        } catch (jsonError) {
          // Handle cases where response body is not valid JSON
          console.error('Failed to parse error response:', jsonError)
          error = {
            error: `HTTP ${response.status}: ${response.statusText || 'Request failed'}`,
            message:
              'The server returned an invalid response. Please try again.',
          }
        }

        // Delete the message (loading or artifact) on error
        deleteMessage(artifactMessageId)

        if (error.key === 'INSUFFICIENT_COINS') {
          pushModal('PricingModal', {
            type: 'out_of_credit',
          })

          addMessage({
            id: nanoid(),
            role: 'system',
            type: 'retry',
          })
          setAiStage('idle')
        } else {
          alertError('response.status !== 200', error)
          monitorResponseFailure(requestId, error, {
            debug: 'response.status !== 200',
          })
        }
        return
      }

      aiStream = response.body
      const contentType = response.headers.get('Content-Type')

      if (!aiStream || contentType === 'application/json') {
        return
      }

      setChatExpanded(true)
      saveMessagesSnapshot()
      setAiStage('coding')

      if (retry) {
        deleteRetryMessage()
      }

      if (isRegenerate) {
        // remove recent messages
        deleteRecentCodingMessages()
      }

      if (addedUserMessage) {
        addMessage(addedUserMessage)
      }

      // Transform loading message into artifact message when starting stream processing
      // On retry, always create a new artifact message since the previous one was deleted
      // For initial page load during 'ing' stage, skip this as messages are already persisted
      const shouldCreateArtifactMessage =
        retry || getInitialCodingStage() !== 'ing'

      if (shouldCreateArtifactMessage) {
        if (messageToUpdate && !retry) {
          // Update the existing loading message to become an artifact
          // (only when not retrying, since on retry the message was deleted)
          updateMessage(artifactMessageId, (message) => {
            message.type = 'artifact'
            message.status = 'loading'
            message.artifact = { actions: [] }
          })
        } else {
          // No existing message to update, or retrying - create new artifact message
          addMessage({
            id: artifactMessageId,
            role: 'system',
            type: 'artifact',
            status: 'loading',
            timestamp: Date.now(),
            artifact: { actions: [] },
          })
        }
      }

      let processText = ''
      // let chunkCount = 0
      // let totalBytesReceived = 0

      // console.log('[retrieveCodingTask] ===== STREAM PARSING START =====')
      // console.log(
      //   '[retrieveCodingTask] Starting parseAIStream with callbacks...',
      // )

      streamContent = await parseAIStream(aiStream, {
        onGetSuperPromptIdsAction: (superPromptIds) => {
          // console.log(
          //   '🚀 [use-ai-coding] onGetSuperPromptIdsAction:',
          //   superPromptIds,
          // )
          setCodingSuperPromptIds(superPromptIds)
        },
        onGetSuperPromptFileNamesAction: (superPromptFileNames) => {
          // console.log(
          //   '🚀 [use-ai-coding] onGetSuperPromptFileNamesAction:',
          //   superPromptFileNames,
          // )
        },
        onGetUserMessage: (userMessage) => {
          // console.log('[retrieveCodingTask] onGetUserMessage received:', {
          //   id: userMessage?.id,
          //   role: userMessage?.role,
          //   type: userMessage?.type,
          //   contentLength: userMessage?.content?.length,
          //   content: `${userMessage?.content?.substring(0, 500)}...`,
          //   hasTemplate: !!userMessage?.template_id,
          //   templateId: userMessage?.template_id,
          //   hasAssets: !!(userMessage?.assets && userMessage.assets.length > 0),
          //   assetsCount: userMessage?.assets?.length || 0,
          // })
          // Artifact message is now added earlier when stream starts
        },
        onGetAssistantMessage: (content) => {
          const cleanContent = cleanAssistantText(content)
          upsertPreviewMessage(cleanContent || content, { isFinal: true })
        },
        onResponse: () => {
          // Streaming text callback - no-op, processing handled in onProcessText
        },
        onAfterArtifactEnd: (content) => {
          const assistantContent = cleanAssistantText(content)
          if (assistantContent) {
            upsertPreviewMessage(assistantContent)
          }
        },
        onArtifactStart: () => {
          // Artifact parsing started
        },
        onActionStart: (action) => {
          updateMessage(artifactMessageId, (message) => {
            // Find existing action by type + path (regardless of status)
            // This handles the case where the same file is updated multiple times
            const existingActionIndex =
              message.artifact?.actions?.findLastIndex(
                (a) => a.type === action.type && a.path === action.path,
              )

            if (existingActionIndex !== undefined && existingActionIndex >= 0) {
              // Reopen existing action - set back to in_progress
              message.artifact!.actions[existingActionIndex].status =
                'in_progress'
              message.artifact!.actions[existingActionIndex].key = action.key
            } else {
              // Add new action
              message.artifact!.actions.push({
                ...action,
                status: 'in_progress',
              })
            }
          })
        },
        onActionEnd: async (action) => {
          updateMessage(artifactMessageId, (message) => {
            const actionIndex = findIndex(message.artifact?.actions || [], {
              key: action.key,
            })
            if (actionIndex >= 0) {
              message.artifact!.actions[actionIndex].status = 'success'
            }
          })
        },
        onProcessText: (_text, chunk) => {
          // Capture raw stream content for debug panel
          const { appendContent, isEnabled } = useStreamDebugStore.getState()
          if (isEnabled) {
            appendContent(chunk)
          }

          const normalizedChunk = chunk.trimStart()
          if (
            !hasFirstResponse &&
            normalizedChunk.toLowerCase().startsWith('<action')
          ) {
            hasFirstResponse = true
            try {
              onFirstResponse?.()
            } catch (error) {
              console.error('onFirstResponse callback failed:', error)
            }
            // console.log('[retrieveCodingTask] First <action chunk detected:', {
            //   preview: normalizedChunk.slice(0, 200),
            //   length: chunk.length,
            // })
          }
          // chunkCount++
          // totalBytesReceived += chunk.length

          // const chunkAnalysis = {
          //   chunkNumber: chunkCount,
          //   chunkLength: chunk.length,
          //   totalAccumulatedLength: _text.length,
          //   totalBytesReceived: totalBytesReceived,
          //   chunkPreview: `${chunk.slice(0, 500)}...`,
          //   flags: {
          //     hasSystemTags: chunk.includes('<System>'),
          //     hasCompletedFlag: chunk.includes(COMPLETED_FLAG),
          //     hasClearResponseFlag: chunk.includes(
          //       '<System>Clear response</System>',
          //     ),
          //     hasCodingSuccessFlag: chunk.includes(CODING_SUCCESS_FLAG),
          //     hasInitializationFlag: chunk.includes(INITIALIZATION_FLAG),
          //     hasUserMessageTag: chunk.includes('<UserMessage>'),
          //     hasArtifactTag: chunk.includes('<artifact>'),
          //     hasActionTag: chunk.includes('<action'),
          //   },
          // }

          // console.log(
          //   `[retrieveCodingTask] Chunk #${chunkCount}:`,
          //   chunkAnalysis,
          // )

          // Log specific flags when detected
          // if (chunkAnalysis.flags.hasCompletedFlag) {
          //   console.log('🚩 [retrieveCodingTask] COMPLETED_FLAG DETECTED!')
          // }
          // if (chunkAnalysis.flags.hasCodingSuccessFlag) {
          //   console.log('✅ [retrieveCodingTask] CODING_SUCCESS_FLAG DETECTED!')
          // }
          // if (chunkAnalysis.flags.hasInitializationFlag) {
          //   console.log('🎯 [retrieveCodingTask] INITIALIZATION_FLAG DETECTED!')
          // }

          const clearFlag = chunk.includes('<System>Clear response</System>')
          if (clearFlag) {
            processText = chunk.split('<System>Clear response</System>')[1]
            // console.log(
            //   '🔄 [retrieveCodingTask] CLEAR RESPONSE detected, resetting processText',
            // )
            // console.log(
            //   '[retrieveCodingTask] New processText preview:',
            //   `${processText.substring(0, 500)}...`,
            // )
          } else {
            processText += chunk
          }

          setProcessingText(
            processText
              .replaceAll(/<System>[\s\S]*?<\/System>/g, '')
              .replaceAll(/<UserMessage>[\s\S]*?<\/UserMessage>/g, ''),
          )

          const assistantPreview = extractAssistantContent(processText)
          upsertPreviewMessage(assistantPreview)

          if (processText.includes(CODING_SUCCESS_FLAG)) {
            isSuccess = true
          }

          if (processText.includes(COMPLETED_FLAG)) {
            isCompleted = true
            streamContent = processText
          }
        },
        onImprovingActionStart: () => {
          // console.log('[retrieveCodingTask] onImprovingActionStart called')
          updateMessage(artifactMessageId, (message) => {
            if (
              !message.artifact?.actions.find(
                (action) => action.type === 'improving',
              )
            ) {
              message.artifact!.actions.push(improvingAction)
            }
          })
        },
        onImprovingActionEnd: () => {
          // console.log('[retrieveCodingTask] onImprovingActionEnd called')
          updateMessage(artifactMessageId, (message) => {
            // Find and update the existing improving action instead of adding a duplicate
            const actions = message.artifact!.actions || []
            const improvingIndex = actions.findIndex(
              (action) => action.type === 'improving',
            )

            if (improvingIndex >= 0) {
              // Update the existing improving action
              actions[improvingIndex] = {
                ...actions[improvingIndex],
                status: 'success',
              }
              message.artifact!.actions = actions
            } else {
              // Only add if it doesn't exist (shouldn't happen normally)
              message.artifact!.actions = [
                ...actions,
                {
                  ...improvingAction,
                  status: 'success',
                },
              ]
            }
          })
        },
      })

      setProcessingText('')

      // Update artifact message status and timestamp when stream completed
      updateMessage(artifactMessageId, (message) => {
        message.status = 'done'
        message.timestamp = Date.now()
      })

      // console.log('[retrieveCodingTask] Final stream analysis:', {
      //   totalChunksReceived: chunkCount,
      //   totalBytesReceived: totalBytesReceived,
      //   finalStreamContentLength: streamContent.length,
      //   streamContentPreview: `${streamContent.substring(0, 500)}...`,
      //   streamEndPreview: streamContent.substring(
      //     Math.max(0, streamContent.length - 500),
      //   ),
      //   contentAnalysis: {
      //     hasUserMessage: streamContent.includes('<UserMessage>'),
      //     userMessageCount: (streamContent.match(/<UserMessage>/g) || [])
      //       .length,
      //     hasSystemTags: streamContent.includes('<System>'),
      //     systemTagCount: (streamContent.match(/<System>/g) || []).length,
      //     hasArtifacts: streamContent.includes('<artifact>'),
      //     artifactCount: (streamContent.match(/<artifact>/g) || []).length,
      //     hasActions: streamContent.includes('<action'),
      //     actionCount: (streamContent.match(/<action/g) || []).length,
      //   },
      //   flags: {
      //     hasCodingSuccess: streamContent.includes(CODING_SUCCESS_FLAG),
      //     hasCompleted: streamContent.includes(COMPLETED_FLAG),
      //     hasInitialization: streamContent.includes(INITIALIZATION_FLAG),
      //     completedFlagPosition: streamContent.indexOf(COMPLETED_FLAG),
      //     codingSuccessFlagPosition: streamContent.indexOf(CODING_SUCCESS_FLAG),
      //   },
      // })

      // Extract and log UserMessage content if present
      // const userMessageMatch = streamContent.match(
      //   /<UserMessage>([\s\S]*?)<\/UserMessage>/,
      // )
      // if (userMessageMatch) {
      //   console.log('[retrieveCodingTask] 📨 Extracted UserMessage:', {
      //     contentLength: userMessageMatch[1].length,
      //     contentPreview: `${userMessageMatch[1].substring(0, 500)}...`,
      //     hasTemplateId: userMessageMatch[1].includes('template_id'),
      //     hasAssets: userMessageMatch[1].includes('assets'),
      //   })

      //   // Try to parse UserMessage as JSON
      //   try {
      //     const userMessageJson = JSON.parse(userMessageMatch[1])
      //     console.log('[retrieveCodingTask] Parsed UserMessage JSON:', {
      //       id: userMessageJson.id,
      //       role: userMessageJson.role,
      //       type: userMessageJson.type,
      //       hasContent: !!userMessageJson.content,
      //       contentLength: userMessageJson.content?.length,
      //       hasTemplateId: !!userMessageJson.template_id,
      //       templateId: userMessageJson.template_id,
      //       hasAssets: !!(
      //         userMessageJson.assets && userMessageJson.assets.length > 0
      //       ),
      //       assetsCount: userMessageJson.assets?.length,
      //     })
      //   } catch (e) {
      //     console.log('[retrieveCodingTask] UserMessage is not valid JSON')
      //   }
      // } else {
      //   console.log('[retrieveCodingTask] ⚠️ No UserMessage found in stream!')
      // }

      // Extract and log any JSON objects
      // const jsonMatches = streamContent.match(/\{"[^"]+":[\s\S]*?\}/g)
      // if (jsonMatches) {
      //   console.log(
      //     '[retrieveCodingTask] 📊 Found JSON objects:',
      //     jsonMatches.length,
      //   )
      //   jsonMatches.forEach((json, index) => {
      //     try {
      //       const parsed = JSON.parse(json)
      //       console.log(
      //         `[retrieveCodingTask] JSON object ${index + 1}:`,
      //         parsed,
      //       )
      //     } catch (e) {
      //       console.log(
      //         `[retrieveCodingTask] JSON object ${index + 1} (unparseable):`,
      //         `${json.substring(0, 500)}...`,
      //       )
      //     }
      //   })
      // }

      // Log the full stream content for debugging
      // console.log('[retrieveCodingTask] 📜 FULL STREAM CONTENT:')
      // console.log('================== START FULL STREAM ==================')
      // console.log(streamContent)
      // console.log('==================  END FULL STREAM  ==================')
      // console.log(
      //   `[retrieveCodingTask] Stream length: ${streamContent.length} bytes`,
      // )

      isSuccess = streamContent.trimEnd().includes(CODING_SUCCESS_FLAG)
      isCompleted = streamContent.includes(COMPLETED_FLAG)
      isInitialization = streamContent.includes(INITIALIZATION_FLAG)
    } catch (error: any) {
      // Only retry if stream wasn't completed
      if (!isCompleted) {
        setAiStage('idle')
        deleteMessage(artifactMessageId)
        await sleep(0.5e3)
        removeTemporaryMessages()
        return await retrieveCodingTask(projectId, { ...options, retry: true })
      }
      // Stream ending after COMPLETED_FLAG is expected
    } finally {
      clearCodingSuperPromptIds()
    }

    if (!isCompleted) {
      await sleep(0.55e3)
      removeTemporaryMessages()
      return await retrieveCodingTask(projectId, { ...options, retry: true })
    }

    if (!isSuccess) {
      if (addedUserMessage) {
        deleteMessage(addedUserMessage.id)
      }
      deleteMessage(artifactMessageId)

      if (!isCanceled.current && !retry) {
        addMessage({
          id: nanoid(),
          role: 'assistant',
          content: t('coding_failed_without_refund'),
          type: 'retry',
        })
        isCanceled.current = false
      }
      setAiStage('idle')

      const initialStage = getInitialCodingStage()
      if (['ing', 'error'].includes(initialStage || '')) {
        window.location.reload()
      }
    } else {
      // Coding successful - complete the flow
      const finishCoding = async () => {
        // Notify the AI waiting modal about completion
        window.postMessage({ type: 'ai-coding-completed' }, '*')
        setChatExpanded(true)
        setAiStage('idle')
      }

      // 从 step-2 后的所有成功步骤
      if (options?.onSuccess) {
        // console.log('[retrieveCodingTask] Calling onSuccess callback')
        options.onSuccess()
      }

      // Add retry logic for checkoutVersion to ensure new version is available
      // console.log(
      //   '[retrieveCodingTask] Checking out version with retry logic...',
      // )
      const maxRetries = 3
      let retryCount = 0
      let checkoutSuccess = false

      while (retryCount < maxRetries && !checkoutSuccess) {
        try {
          // Wait before checkout to allow backend to save the new version
          const waitTime = retryCount === 0 ? 500 : 1000 * 2 ** (retryCount - 1)
          // console.log(
          //   `[retrieveCodingTask] Waiting ${waitTime}ms before checkout attempt ${retryCount + 1}/${maxRetries}`,
          // )
          await sleep(waitTime)

          // Get current version count before checkout
          const currentVersionCount = Object.keys(
            project?.versions || {},
          ).length
          // console.log(
          //   `[retrieveCodingTask] Current version count before checkout: ${currentVersionCount}`,
          // )

          await checkoutVersion()

          // Verify new version was retrieved
          const updatedProject = getProject()
          const newVersionCount = Object.keys(
            updatedProject?.versions || {},
          ).length
          const currentIndex = updatedProject?.file_system_tree_index || 0

          // console.log(
          //   `[retrieveCodingTask] After checkout - Version count: ${newVersionCount}, Current index: ${currentIndex}`,
          // )

          // Check if we have a new version or updated content
          if (newVersionCount > currentVersionCount || currentIndex > 0) {
            // console.log(
            //   '[retrieveCodingTask] ✅ Checkout successful - new version detected',
            // )
            checkoutSuccess = true
          } else if (retryCount === maxRetries - 1) {
            // On last retry, consider it successful if we at least have a project
            // console.log(
            //   '[retrieveCodingTask] ⚠️ No new version detected, but proceeding after final retry',
            // )
            checkoutSuccess = true
          } else {
            // console.log(
            //   '[retrieveCodingTask] ⚠️ No new version detected, will retry...',
            // )
            retryCount++
          }
        } catch (error) {
          console.error(
            `[retrieveCodingTask] Error during checkout attempt ${retryCount + 1}:`,
            error,
          )
          retryCount++
          if (retryCount >= maxRetries) {
            // console.log(
            //   '[retrieveCodingTask] Max retries reached, proceeding anyway',
            // )
            checkoutSuccess = true
          }
        }
      }

      // Add building action to show progress during build phase
      const buildingAction: ArtifactAction = {
        key: nanoid(),
        content: 'Building preview...',
        status: 'in_progress',
        type: 'building',
      }
      updateMessage(artifactMessageId, (message) => {
        message.artifact!.actions.push(buildingAction)
      })

      // Wait for preview/build to complete before showing celebration
      const previewResult = await updatePreview({ useCachedUrl: true }).catch(
        () => {
          return { isSuccess: false }
        },
      )

      // Mark building as complete, add finalizing action
      updateMessage(artifactMessageId, (message) => {
        const buildingIndex = message.artifact?.actions.findIndex(
          (action) => action.type === 'building',
        )
        if (buildingIndex !== undefined && buildingIndex >= 0) {
          message.artifact!.actions[buildingIndex].status = 'success'
        }
      })

      // Add finalizing action
      const finalizingAction: ArtifactAction = {
        key: nanoid(),
        content: 'Final touches...',
        status: 'in_progress',
        type: 'finalizing',
      }
      updateMessage(artifactMessageId, (message) => {
        message.artifact!.actions.push(finalizingAction)
      })

      // Reload preview page after build completes
      const { reloadPreviewPage } = usePreviewStore.getState()
      reloadPreviewPage()

      // Mark finalizing as complete
      updateMessage(artifactMessageId, (message) => {
        const finalizingIndex = message.artifact?.actions.findIndex(
          (action) => action.type === 'finalizing',
        )
        if (finalizingIndex !== undefined && finalizingIndex >= 0) {
          message.artifact!.actions[finalizingIndex].status = 'success'
        }
      })

      // Only play ding sound and confetti AFTER build succeeds (for initialization)
      if (isInitialization && previewResult?.isSuccess !== false) {
        // console.log(
        //   '[retrieveCodingTask] 🎉 Playing confetti and sound after build success',
        // )
        playSound('complete.mp3')
        playConfetti(3e3)
        if (!didTrackProjectGenerationRef.current) {
          const userId = user?._id?.toString()
          sendToBothAnalytics(
            MIXPANEL_CLIENT_EVENT_NAMES.PROJECT_GENERATION_CONFIRMED,
            {
              project_id: currentProjectId,
              project_name: project?.project_name,
              workspace_id: project?.workspace_id,
            },
            userId,
          )
          didTrackProjectGenerationRef.current = true
        }
      }

      // Finish coding sequence after build is ready
      await finishCoding()

      // Force a project refresh if needed
      const finalProject = getProject()
      // console.log('[retrieveCodingTask] Final project state:', {
      //   hasFileSystemTree: !!finalProject?.file_system_tree,
      //   fileSystemTreeIndex: finalProject?.file_system_tree_index,
      //   fileSystemTreeListLength: finalProject?.file_system_tree_list?.length,
      //   versionCount: Object.keys(finalProject?.versions || {}).length,
      //   versions: finalProject?.versions,
      // })

      if (
        !finalProject?.file_system_tree ||
        finalProject.file_system_tree_index === 0
      ) {
        // console.log(
        //   '[retrieveCodingTask] ⚠️ File system tree not updated, forcing project refresh...',
        // )
        await mutate(`/api/projects/${projectId}`)

        // After refresh, check again
        // const refreshedProject = getProject()
        // console.log('[retrieveCodingTask] After refresh:', {
        //   hasFileSystemTree: !!refreshedProject?.file_system_tree,
        //   fileSystemTreeIndex: refreshedProject?.file_system_tree_index,
        //   fileSystemTreeListLength:
        //     refreshedProject?.file_system_tree_list?.length,
        //   versionCount: Object.keys(refreshedProject?.versions || {}).length,
        // })
      }
    }

    // update user to have latest coin
    await mutate('/api/me')

    // retrieve for queue next task
    retrieveCodingTask(projectId)
  }

  const stop = async () => {
    if (!projectId) {
      return
    }
    isCanceled.current = true
    await fetch(`/api/coding/${projectId}`, {
      method: 'DELETE',
    })
    abortController.current.abort()
    restoreMessagesSnapshot()
    removeTemporaryMessages()
    setAiStage('idle')
    setStage('running')
    setIsFixingIssue(false)
    // console.log('cancelled coding')

    // 停止 coding 后，清空 coding 中的 super prompt ids
    clearCodingSuperPromptIds()
  }

  return { aiCoding, retrieveCodingTask, stop }
}
